// Lazy-loads a worker's photo from /api/cases/{id}/photo.
// - Zero cost if the worker object already has photoData/workerPhoto (backward compat).
// - Otherwise fetches once and caches in a module-level Map so the same photo
//   never gets fetched twice within a session.
// - Supports live cache updates (e.g. after a photo upload) via setWorkerPhotoCache,
//   which instantly re-renders every mounted component showing that worker's photo.
// - Returns the ready-to-use image src (data URL or empty string).

import { useEffect, useState } from 'react';

const PHOTO_CACHE = new Map();
const INFLIGHT = new Map();
const LISTENERS = new Map(); // workerId -> Set<setSrc>

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const normalize = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  if (raw.startsWith('data:image')) return raw;
  return `data:image/jpeg;base64,${raw}`;
};

const notify = (id, value) => {
  const subs = LISTENERS.get(id);
  if (subs) subs.forEach((cb) => cb(value));
};

// Update the cache after a photo upload/replace so every mounted <WorkerPhoto>
// for this worker refreshes immediately (no reload, no stale browser cache).
export const setWorkerPhotoCache = (id, raw) => {
  if (!id) return;
  const value = normalize(raw);
  PHOTO_CACHE.set(id, value);
  notify(id, value);
};

export const clearWorkerPhotoCache = (id) => {
  if (!id) return;
  PHOTO_CACHE.delete(id);
  notify(id, '');
};

export const useWorkerPhoto = (worker) => {
  const inline = worker?.photoData || worker?.workerPhoto || '';
  const initial = inline ? normalize(inline) : (PHOTO_CACHE.get(worker?.id) || '');
  const [src, setSrc] = useState(initial);

  // Subscribe to live cache updates for this worker id.
  useEffect(() => {
    const id = worker?.id;
    if (!id) return undefined;
    let subs = LISTENERS.get(id);
    if (!subs) {
      subs = new Set();
      LISTENERS.set(id, subs);
    }
    subs.add(setSrc);
    // Sync with any value cached since first render.
    if (PHOTO_CACHE.has(id)) setSrc(PHOTO_CACHE.get(id));
    return () => {
      subs.delete(setSrc);
      if (subs.size === 0) LISTENERS.delete(id);
    };
  }, [worker?.id]);

  useEffect(() => {
    if (!worker) return undefined;
    if (worker.photoData || worker.workerPhoto) {
      setSrc(normalize(worker.photoData || worker.workerPhoto));
      return undefined;
    }
    if (!worker.has_photo || !worker.id) return undefined;
    if (PHOTO_CACHE.has(worker.id)) {
      setSrc(PHOTO_CACHE.get(worker.id));
      return undefined;
    }
    // Fetch once (dedupe via INFLIGHT)
    let cancelled = false;
    const load = async () => {
      let promise = INFLIGHT.get(worker.id);
      if (!promise) {
        const token = localStorage.getItem('authToken');
        promise = fetch(`${BACKEND_URL}/api/cases/${worker.id}/photo`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
          .then((r) => (r.ok ? r.json() : { photo_data: '' }))
          .then((j) => normalize(j.photo_data || ''))
          .catch(() => '');
        INFLIGHT.set(worker.id, promise);
      }
      try {
        const data = await promise;
        PHOTO_CACHE.set(worker.id, data);
        if (!cancelled) setSrc(data);
      } finally {
        INFLIGHT.delete(worker.id);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [worker?.id, worker?.has_photo, worker?.photoData, worker?.workerPhoto]);

  return src;
};

// Component wrapper — drop-in replacement for <img> when displaying worker photos
export const WorkerPhoto = ({ worker, alt, className, fallback = null, ...rest }) => {
  const src = useWorkerPhoto(worker);
  if (!src) return fallback;
  return <img src={src} alt={alt || worker?.workerName || 'worker'} className={className} {...rest} />;
};
